import {
  Dialog,
  Button,
  Typography,
  Box,
  TextField,
  Checkbox,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ja } from "date-fns/locale";
import { RemarksTemplateDialogStyle as styles } from "./RemarksTemplateDialogStyle";
import {
  ResponseGetRemarksTemplateResponse,
  ResponseLoginResponse,
} from "../services/OpenAPI";
import { templateType } from "../constants/templateType";

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (selectItem: ResponseGetRemarksTemplateResponse) => void;
  list: ResponseGetRemarksTemplateResponse[];
  mode: templateType;
  setMode: React.Dispatch<React.SetStateAction<templateType>>;
  onCreate?: (title: string, template: string) => void;
  onUpdate?: (id: number, title: string, template: string) => void;
  onDelete?: (ids: number[]) => void;
  loginUser: ResponseLoginResponse | null;
};

const RemarksTemplateDialog: React.FC<Props> = ({
  open,
  list,
  onClose,
  onSelect,
  mode,
  setMode,
  onCreate,
  onUpdate,
  onDelete,
  loginUser,
}) => {
  const [title, setTitle] = useState("");
  const [template, setTemplate] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const handleCreate = () => {
    if (onCreate) {
      onCreate(title, template);
    }
    setTitle("");
    setTemplate("");
    setSelectedIds([]);
  };

  const handleUpdate = () => {
    if (onUpdate && selectedIds.length > 0) {
      onUpdate(selectedIds[0], title, template);
    }
    setTitle("");
    setTemplate("");
    setSelectedIds([]);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(selectedIds);
    }
  };

  useEffect(() => {
    if (!open) {
      setTitle("");
      setTemplate("");
      setSelectedIds([]);
      setMode(templateType.LIST);
    }
  }, [open]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Dialog
        open={open}
        onClose={onClose}
        disableEnforceFocus
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            display: "flex",
            width: mode === templateType.LIST ? "400px" : "600px",
            height: "500px",
            maxWidth: "none",
            maxHeight: "none",
            overflowY: "hidden",
          },
        }}
        sx={{ zIndex: 2001 }}
      >
        <Dialog
          open={confirmDeleteOpen}
          onClose={() => setConfirmDeleteOpen(false)}
          sx={{ zIndex: 2002 }}
        >
          <Box sx={{ p: 3 }}>
            <Typography>本当に削除しますか？</Typography>

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => {
                  // 削除処理を親に渡すなら props に onDelete を追加
                  console.log("削除するID:", selectedIds);
                  handleDelete();
                  setConfirmDeleteOpen(false);
                  setSelectedIds([]);
                }}
              >
                はい
              </Button>

              <Button
                variant="outlined"
                onClick={() => setConfirmDeleteOpen(false)}
              >
                いいえ
              </Button>
            </Box>
          </Box>
        </Dialog>
        {/* ヘッダー */}
        <Box sx={styles.header}>
          <Typography sx={styles.headerText}>
            {mode === templateType.LIST
              ? "テンプレート一覧"
              : "テンプレート登録"}
          </Typography>
        </Box>

        {/* 本体 */}
        <Box
          sx={{
            ...styles.formArea,
            // flex: 1, // ★残り高さを全部使う
            // overflowY: "auto", // ★赤枠部分だけスクロール
            // overflowX: "hidden",
          }}
        >
          {mode === templateType.LIST && (
            <>
              <Box
                sx={{
                  ...styles.topArea,
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    // 新規登録
                    setTitle("");
                    setTemplate("");
                    setMode(templateType.CREATE);
                  }}
                  sx={styles.orangeButton}
                >
                  新規登録
                </Button>
                <Button
                  variant="outlined"
                  disabled={selectedIds.length !== 1}
                  onClick={() => {
                    const target = list.find((x) => x.id === selectedIds[0]);
                    if (!target) return;

                    setTitle(target.title ?? "");
                    setTemplate(target.template ?? "");
                    setMode(templateType.EDIT);
                  }}
                >
                  編集
                </Button>
                <Button
                  variant="outlined"
                  disabled={selectedIds.length === 0}
                  onClick={() => setConfirmDeleteOpen(true)}
                  color="error"
                >
                  削除
                </Button>
              </Box>
              <Box
                sx={{
                  overflowY: "auto",
                  overflowX: "hidden",
                  maxHeight: 350,
                  px: 2,
                  width: "90%",
                }}
              >
                {list.map((item) => {
                  const checked = selectedIds.includes(item.id ?? 0);

                  const shouldShowCheckbox =
                    loginUser?.id === 1 ||
                    loginUser?.id === 2 ||
                    ![1, 2, 3].includes(item.id ?? 0);

                  return (
                    <Box
                      key={item.id}
                      sx={{ display: "flex", alignItems: "center", mb: 1 }}
                    >
                      {shouldShowCheckbox ? (
                        <Checkbox
                          checked={checked}
                          onChange={() => {
                            setSelectedIds((prev) =>
                              checked
                                ? prev.filter((id) => id !== item.id)
                                : item.id !== undefined
                                  ? [...prev, item.id]
                                  : prev,
                            );
                          }}
                        />
                      ) : (
                        // ★ チェックボックスと同じ幅のダミー
                        <Box sx={{ width: 42, height: 42 }} />
                      )}

                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => onSelect(item)}
                        sx={{
                          justifyContent: "flex-center",
                          textAlign: "center",
                          width: 250,
                          ml: 1,
                        }}
                      >
                        {item.title}
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {(mode === templateType.CREATE || mode === templateType.EDIT) && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                type="text"
                placeholder="テンプレート名"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{ width: "500px" }}
              />

              <TextField
                placeholder="テンプレート本文"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                multiline
                minRows={10}
                style={{ width: "500px" }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  gap: 2,
                  mt: 1,
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => {
                    if (mode === templateType.CREATE) {
                      handleCreate();
                    } else if (mode === templateType.EDIT) {
                      handleUpdate();
                    }
                    setTitle("");
                    setTemplate("");
                    setSelectedIds([]);
                    setMode(templateType.LIST);
                  }}
                  sx={styles.orangeButton}
                >
                  {mode === templateType.CREATE ? "登録" : "更新"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setTitle("");
                    setTemplate("");
                    setSelectedIds([]);
                    setMode(templateType.LIST);
                  }}
                >
                  キャンセル
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>
    </LocalizationProvider>
  );
};

export default RemarksTemplateDialog;
